export type EdgeCallPolicy = "interactive" | "background" | "fire-and-forget";

export const EDGE_FUNCTION_NAMES = [
  "admin-platform-management",
  "admin-reset-mfa",
  "ap-inbound-email",
  "ap-process-inbound",
  "admin-revoke-sessions",
  "admin-user-management",
  "auth-login",
  "auth-refresh",
  "mfa-session-state",
  "auth-unlink-identity",
  "bootstrap-org-owner",
  "ccli-connect",
  "ccli-submit-report",
  "cleanup-orphaned-attachments",
  "comm-cron-reminders",
  "comm-cron-scheduled",
  "comm-digest",
  "comm-dispatch-email",
  "comm-dispatch-sms",
  "comm-manage-domain",
  "comm-send",
  "comm-webhook-resend",
  "comm-webhook-twilio",
  "db-sync",
  "demo-seed",
  "generate-giving-statement-pdf",
  "forge-form-public-config",
  "forge-form-submit",
  "forge-document-create",
  "forge-document-view-public",
  "forge-document-accept",
  "forge-document-pdf",
  "forge-document-send-email",
  "google-calendar-auth-callback",
  "google-calendar-auth-start",
  "inbound-sms",
  "integration-connect",
  "integration-disconnect",
  "integration-dlq-resolve",
  "integration-health",
  "integration-sync",
  "integration-webhook",
  "invite-person",
  "job-runner",
  "link-invite-person",
  "m365-calendar-auth-callback",
  "m365-calendar-auth-start",
  "manage-push-subscription",
  "push-register-device",
  "live-activity-push",
  "checkin-session",
  "app-intents-data",
  "care-queue-inputs",
  "notifications-health",
  "org-reset-password",
  "process-critical-notifications",
  "process-recurring-gifts",
  "process-report-subscriptions",
  "process-request-escalations",
  "process-scheduled-messages",
  "public-giving",
  "send-member-magic-link",
  "public-giving-link",
  "public-group-signup",
  "public-join-request",
  "public-widgets",
  "qbo-api",
  "qbo-auth-callback",
  "qbo-auth-start",
  "qbo-disconnect",
  "qbo-export",
  "retry-report-subscription",
  "run-automations",
  "run-calendar-sync",
  "run-finance-sync",
  "run-scheduled-syncs",
  "search-filter",
  "send-article-email",
  "send-followup-reminders",
  "send-invite",
  "send-receipt",
  "send-sms",
  "send-transactional-email",
  "send-volunteer-notification",
  "solomon-chat",
  "solomon-plan",
  "sms-webhook",
  "songselect-planning-list",
  "songselect-search",
  "stripe-backfill",
  "stripe-billing-create-checkout",
  "stripe-billing-customer-portal",
  "stripe-billing-process-event",
  "stripe-connect-express-onboard",
  "stripe-connect-express-process-event",
  "stripe-connect-express-refresh",
  "stripe-connect-express-status",
  "stripe-connect-standard-onboard",
  "stripe-connect-standard-start",
  "stripe-connect-standard-callback",
  "stripe-connect-disconnect",
  "stripe-create-checkout",
  "stripe-payment-methods",
  "stripe-fc-complete-link",
  "stripe-fc-create-session",
  "stripe-fc-disconnect",
  "stripe-fc-sync-transactions",
  "stripe-process-event",
  "submit-error-report",
  "submit-public-request",
  "request-account-recovery",
  "verify-account-recovery",
  "validate-invite",
  "workflow-runner",
  "zoho-api",
  "zoho-auth-callback",
  "zoho-auth-start",
  "zoho-save-credentials",
  "zoho-sync",
  // Relay self-serve SMS onboarding (Twilio subaccount/number provisioning)
  "relay-sms-onboarding-start",
  "relay-sms-onboarding-provision-number",
  "relay-sms-onboarding-status",
  // Native companion app (NEDGE) — mobile edge functions
  "push-register-device",
  "checkin-session",
  "app-intents-data",
] as const;

export type EdgeFunctionName = (typeof EDGE_FUNCTION_NAMES)[number];

export type EdgeFunctionMetadata = {
  name: EdgeFunctionName;
  authRequired: boolean;
  policy: EdgeCallPolicy;
  retries: number;
  /**
   * Roughly how long a *healthy* call takes. Telemetry and budgeting only —
   * this is deliberately NOT the request deadline. See `deriveTimeoutMs`.
   */
  expectedLatencyMs: number;
  /**
   * Hard deadline override, for the rare function whose real ceiling is known
   * and unrelated to its typical latency. Leave unset and the deadline is
   * derived from `expectedLatencyMs` with headroom.
   */
  timeoutMs?: number;
  userMessageOnFailure: string;
  owner: string;
  /**
   * Whether an identical request may be transmitted a second time.
   *
   * Defaults to NOT replay-safe (#5970). A non-2xx from an edge function is
   * the endpoint's own answer, so `invokeEdgeFunction` returns it, with the
   * response body as the error's `details`, and sends nothing again. Set
   * `true` only for an endpoint where receiving the identical request twice
   * has no effect beyond the first; `invokeEdgeFunction` then re-sends the
   * same body over plain fetch after a non-2xx. Check first that it spends no
   * single-use token or code, sends no message a person receives without its
   * own dedupe key, moves no money, writes no audit or ledger row keyed on
   * anything but an idempotency key, and creates nothing a webhook-style caller
   * would upsert (#6116); then add its name to `AUDITED_REPLAY_SAFE` in
   * edge-functions.replay-safety.test.ts, which fails for any unlisted one.
   * No endpoint is declared today.
   *
   * This flag gates only that fallback. The retry loop (429/502/503/504 or no
   * status, `retries` > 0, non-interactive policy) is separate and never reads
   * it; see #8464.
   *
   * The explicit `false` entries below predate that default and are kept as
   * the record of why each must never be replayed. They are the cases where
   * a second delivery of the SAME body is destructive on its own — not merely
   * wasteful. `retries` does NOT cover this: for
   * `policy: "interactive"` retries are already forced to zero, and the
   * fallback never consults `retries`, `allowRetry`, or `policy` at all. That
   * gap is not theoretical; see the note on `stripe-fc-complete-link` below
   * (#6110, #6139) — the OAuth endpoint that originally surfaced it,
   * `stripe-connect-standard-callback`, exchanges a single-use authorization
   * code. Its `replaySafe: false` must remain in place for existing-account linking.
   *
   * Also set it to `false` when the CALLER already implements its own outer
   * retry loop over the whole logical operation (see `stripe-fc-complete-link`
   * below, #6139 round 7): the generic fallback then adds no real resilience
   * — the caller's loop already covers that — while silently doubling every
   * non-2xx attempt's physical request count. That is pure waste ordinarily,
   * but on a rate-limited endpoint it can consume the caller's own retry
   * budget before the caller ever gets to use it.
   *
   * Scope: this gates the FunctionsHttpError fallback. It deliberately does
   * NOT gate the auth-refresh `continue`, which also replays the whole attempt
   * on a 401. Gating that too was tried and reverted (Codex P2, PR #6120), on
   * the strength of `stripe-connect-standard-callback`: its 401s were
   * raised only from the authorization-header and
   * `getUser()` checks — both of which run before the body is parsed, before
   * its single-use resource was claimed, and before Stripe was called — so a
   * refresh-and-retry there could not spend that resource twice. Suppressing
   * the auth-refresh retry only stranded the caller, because the client marks
   * its own submission before the request and so refuses to retry, forcing a
   * whole new round trip with a resource the server still considers valid.
   *
   * A future `replaySafe: false` endpoint that can answer 401 AFTER consuming
   * its single-use resource would need that path gated as well. Check where
   * the endpoint's 401s are raised before relying on this.
   */
  replaySafe?: boolean;
};

/**
 * A timeout is not an expectation.
 *
 * `expectedLatencyMs` used to be passed straight through as the request
 * deadline, so every call was aborted at exactly the duration it was expected
 * to take — leaving zero headroom and failing any run at or above its own
 * expectation. That is what surfaced as "Edge function request timed out after
 * 5000ms" on the sign-in screen: `auth-login` expects 5s and was given 5s, on
 * a phone, with `policy: "interactive"` forcing zero retries.
 *
 * The deadline is now derived with headroom, mirroring the server/client split
 * already used in `timing-constants.ts` (`ROS_*`, `PLANNING_*`, `MATRIX_*`),
 * where the client budget is about twice the server's.
 */
export const EDGE_TIMEOUT_LATENCY_MULTIPLIER = 2;

/**
 * Upper bound so a long-running expectation cannot hold a request open
 * indefinitely. Never applied below the expectation itself — see below.
 */
export const EDGE_TIMEOUT_CEILING_MS = 60_000;

/**
 * Derive a request deadline from an expected latency.
 *
 * Guarantees, in order of precedence:
 *   1. Never shorter than `expectedLatencyMs`. The ceiling must not reintroduce
 *      the very abort this function exists to prevent, so an entry whose
 *      expectation exceeds the ceiling still gets the time it asks for.
 *   2. Never shorter than `minTimeoutMs`, the platform floor — a phone on a bad
 *      network needs more than a desktop regardless of the function.
 *   3. Otherwise `expectedLatencyMs * EDGE_TIMEOUT_LATENCY_MULTIPLIER`, capped.
 *
 * A missing or nonsensical expectation falls back to the floor rather than
 * producing `NaN`, which `setTimeout` would treat as 0 and fire immediately.
 */
export function deriveTimeoutMs(expectedLatencyMs: number | undefined, minTimeoutMs: number): number {
  const expected =
    typeof expectedLatencyMs === "number" && Number.isFinite(expectedLatencyMs) && expectedLatencyMs > 0
      ? expectedLatencyMs
      : 0;
  const withHeadroom = Math.min(expected * EDGE_TIMEOUT_LATENCY_MULTIPLIER, EDGE_TIMEOUT_CEILING_MS);
  return Math.max(withHeadroom, expected, minTimeoutMs);
}

const DEFAULT_REGISTRY_ENTRY: Omit<EdgeFunctionMetadata, "name"> = {
  authRequired: true,
  policy: "interactive",
  retries: 0,
  expectedLatencyMs: 12_000,
  userMessageOnFailure: "Unable to complete this request. Please try again.",
  owner: "platform",
};

type EdgeFunctionMetadataOverrides = Partial<
  Omit<EdgeFunctionMetadata, "name">
>;

const EDGE_FUNCTION_OVERRIDES: Partial<Record<EdgeFunctionName, EdgeFunctionMetadataOverrides>> = {
  "relay-sms-onboarding-start": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    expectedLatencyMs: 15_000,
    owner: "relay",
    userMessageOnFailure: "Couldn't set up your SMS account. Please try again.",
  },
  "relay-sms-onboarding-provision-number": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    expectedLatencyMs: 25_000,
    owner: "relay",
    userMessageOnFailure: "Couldn't get you a phone number. Please try again or pick a different area code.",
  },
  "relay-sms-onboarding-status": {
    authRequired: true,
    policy: "background",
    retries: 1,
    expectedLatencyMs: 8_000,
    owner: "relay",
    userMessageOnFailure: "Couldn't check SMS setup status.",
  },
  "ap-inbound-email": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 3,
    expectedLatencyMs: 5000,
    owner: "finance",
    userMessageOnFailure: "Inbound email processing failed.",
  },
  "ap-process-inbound": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 30_000,
    owner: "finance",
    userMessageOnFailure: "Bill processing from email failed.",
  },
  "auth-login": {
    authRequired: false,
    owner: "auth",
    userMessageOnFailure: "Sign-in failed. Please check your credentials and try again.",
    expectedLatencyMs: 5000,
  },
  /** @deprecated — No longer called after @supabase/ssr migration. Kept for in-flight compatibility. */
  "auth-refresh": {
    authRequired: false,
    policy: "interactive",
    retries: 1,
    userMessageOnFailure: "Session refresh failed. Please sign in again.",
    owner: "auth",
  },
  "mfa-session-state": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 5000,
    owner: "auth",
    userMessageOnFailure: "Unable to verify MFA session state. Please try again.",
  },
  "push-register-device": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 8000,
    owner: "native",
    userMessageOnFailure: "Device push registration could not be synced.",
  },
  "live-activity-push": {
    authRequired: true,
    policy: "background",
    retries: 1,
    expectedLatencyMs: 8000,
    owner: "native",
    userMessageOnFailure: "Live Activity readiness could not be verified.",
  },
  "checkin-session": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 10_000,
    owner: "native",
    userMessageOnFailure: "Check-in could not be confirmed. Please try again.",
  },
  "app-intents-data": {
    authRequired: true,
    policy: "background",
    retries: 1,
    expectedLatencyMs: 8000,
    owner: "native",
    userMessageOnFailure: "Shortcut data could not be refreshed.",
  },
  "care-queue-inputs": {
    authRequired: true,
    policy: "background",
    retries: 1,
    expectedLatencyMs: 10_000,
    owner: "native",
    userMessageOnFailure: "Care queue inputs could not be refreshed.",
  },
  "public-giving": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 5000,
    owner: "giving",
    userMessageOnFailure: "The giving form is currently unavailable. Please retry.",
  },
  "send-member-magic-link": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 5000,
    owner: "auth",
    // Generic copy — the endpoint itself returns a generic message
    // on both success and failure so we don't leak existence; the
    // UI shows that string verbatim.
    userMessageOnFailure: "We couldn't process that request. Please retry in a moment.",
  },
  "public-group-signup": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 5000,
    owner: "giving",
    userMessageOnFailure: "Group signup is temporarily unavailable. Please try again.",
  },
  "public-join-request": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 5000,
    owner: "membership",
    userMessageOnFailure: "Unable to submit your join request right now.",
  },
  "public-widgets": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 5000,
    owner: "public",
    userMessageOnFailure: "Unable to load this public page right now.",
  },
  "forge-form-public-config": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 5000,
    owner: "forms",
    userMessageOnFailure: "This form is currently unavailable. Please try again.",
  },
  "forge-form-submit": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 8000,
    owner: "forms",
    userMessageOnFailure: "Unable to submit this form right now. Please try again.",
  },
  "forge-document-create": {
    authRequired: true,
    policy: "interactive",
    expectedLatencyMs: 8000,
    owner: "documents",
    userMessageOnFailure: "Unable to create this document. Please try again.",
  },
  "forge-document-view-public": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 5000,
    owner: "documents",
    userMessageOnFailure: "Unable to load this document. Please try again.",
  },
  "forge-document-accept": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 8000,
    owner: "documents",
    userMessageOnFailure: "Unable to acknowledge this document. Please try again.",
  },
  "forge-document-pdf": {
    authRequired: false,
    policy: "background",
    expectedLatencyMs: 12_000,
    owner: "documents",
    userMessageOnFailure: "Unable to render this document PDF. Please try again.",
  },
  "forge-document-send-email": {
    authRequired: true,
    policy: "interactive",
    expectedLatencyMs: 30_000,
    owner: "documents",
    userMessageOnFailure: "Unable to send this document email. Please try again.",
  },
  "submit-public-request": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 8000,
    owner: "public",
    userMessageOnFailure: "Unable to send your request. Please retry.",
  },
  "validate-invite": {
    authRequired: false,
    expectedLatencyMs: 6000,
    owner: "membership",
  },
  "request-account-recovery": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 8000,
    owner: "membership",
    userMessageOnFailure: "Unable to send a recovery code right now. Please try again.",
  },
  "verify-account-recovery": {
    authRequired: false,
    policy: "interactive",
    expectedLatencyMs: 8000,
    owner: "membership",
    userMessageOnFailure: "Unable to verify that code right now. Please try again.",
  },
  "inbound-sms": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 3,
    expectedLatencyMs: 8000,
    owner: "communications",
    userMessageOnFailure: "Inbound SMS processing is delayed.",
  },
  "sms-webhook": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 4,
    expectedLatencyMs: 8000,
    owner: "communications",
    userMessageOnFailure: "SMS webhook processing is delayed.",
  },
  "comm-webhook-resend": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 9000,
    owner: "communications",
    userMessageOnFailure: "Webhook resend is retrying.",
  },
  "comm-webhook-twilio": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 4,
    expectedLatencyMs: 8000,
    owner: "communications",
    userMessageOnFailure: "Twilio webhook processing is delayed.",
  },
  "comm-cron-reminders": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 10_000,
    owner: "communications",
    userMessageOnFailure: "Reminder job is being retried.",
  },
  "comm-cron-scheduled": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 10_000,
    owner: "communications",
  },
  "comm-digest": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 10_000,
    owner: "communications",
  },
  "comm-dispatch-email": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 12_000,
    owner: "communications",
  },
  "comm-manage-domain": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 10_000,
    // The wizard's test send (send_test_email) delivers a real email, and add/remove are not
    // idempotent either, so a non-2xx must not be re-sent over the plain-fetch fallback: that
    // would send a second message, or a second provider call, for one click. The endpoint's own
    // message is still surfaced (see edge-functions.replay-safety.test.ts).
    replaySafe: false,
    owner: "communications",
    userMessageOnFailure: "Unable to manage email domain. Please try again.",
  },
  "job-runner": {
    authRequired: true,
    policy: "background",
    retries: 1,
    expectedLatencyMs: 30_000,
    owner: "operations",
    userMessageOnFailure: "Background job dispatcher is being retried.",
  },
  "workflow-runner": {
    authRequired: false,
    policy: "background",
    retries: 1,
    expectedLatencyMs: 30_000,
    owner: "automation",
    userMessageOnFailure: "Workflow runner is being retried.",
  },
  "solomon-chat": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    // Slice 2 added a bounded tool-use loop: up to 5 sequential Anthropic calls
    // (maxIters=4) when answering data questions, vs. a single call for doc/how-to
    // questions. Raise the ceiling well above the 12s default so a legitimate
    // multi-tool answer isn't aborted client-side mid-flight.
    //
    // This 45s was chosen as a deliberate *ceiling*, not a typical latency, so it
    // is pinned with an explicit timeoutMs rather than derived. Deriving would
    // stretch it to the 60s cap and make an interactive chat hang half a minute
    // longer than its author intended. The expectation is left at the same value
    // because no measured typical latency is recorded for this endpoint.
    expectedLatencyMs: 45_000,
    timeoutMs: 45_000,
    owner: "solomon",
    userMessageOnFailure:
      "Solomon couldn't answer that right now. Please try again.",
  },
  "solomon-plan": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    // Anthropic Sonnet 4 typically returns in 2-6s for a single tool
    // call. The 15s ceiling leaves slack for the rare slow planner
    // pass without making the UI hang indefinitely.
    //
    // That slack is already baked in, so this is a *ceiling* and is pinned with
    // an explicit timeoutMs. Deriving would double it to 30s and re-introduce
    // exactly the indefinite-feeling hang the comment above rules out.
    expectedLatencyMs: 15_000,
    timeoutMs: 15_000,
    owner: "solomon",
    userMessageOnFailure:
      "Solomon couldn't plan that request right now. Please try again.",
  },
  "comm-dispatch-sms": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 12_000,
    owner: "communications",
  },
  "notifications-health": {
    authRequired: true,
    policy: "background",
    retries: 1,
    expectedLatencyMs: 5000,
    owner: "communications",
    userMessageOnFailure: "Notifications synthetic health probe failed.",
  },
  "process-critical-notifications": {
    authRequired: true,
    policy: "fire-and-forget",
    retries: 3,
    expectedLatencyMs: 12_000,
    owner: "operations",
  },
  "process-request-escalations": {
    authRequired: true,
    policy: "fire-and-forget",
    retries: 3,
    expectedLatencyMs: 12_000,
    owner: "operations",
  },
  "process-scheduled-messages": {
    authRequired: true,
    policy: "fire-and-forget",
    retries: 3,
    expectedLatencyMs: 12_000,
    owner: "communications",
  },
  "process-recurring-gifts": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 12_000,
    owner: "payments",
  },
  "retry-report-subscription": {
    authRequired: true,
    policy: "background",
    // Each delivery force-retries every failed schedule it is given, and a
    // schedule can fail AFTER queuing its message to some recipients. A second
    // delivery of the same request would queue those recipients again, so a
    // non-2xx must reach the admin, not be replayed: `replaySafe: false` stops
    // the FunctionsHttpError fallback and `retries: 0` stops the 502/503/504
    // retry loop (Codex P2, PR #8240). The admin can press Retry again.
    replaySafe: false,
    retries: 0,
    expectedLatencyMs: 10_000,
    owner: "operations",
  },
  "run-automations": {
    authRequired: true,
    policy: "fire-and-forget",
    retries: 2,
    expectedLatencyMs: 12_000,
    owner: "operations",
  },
  "send-followup-reminders": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 10_000,
    owner: "communications",
  },
  "cleanup-orphaned-attachments": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 12_000,
    owner: "operations",
  },
  "db-sync": {
    authRequired: true,
    policy: "background",
    // An incomplete export or a partial import now answers non-2xx (#6454).
    // That result is deterministic, so replaying the request would only rerun
    // a whole export or import, up to 50 MB, to get the same answer:
    // `replaySafe: false` stops the FunctionsHttpError fallback, and
    // `retries: 0` stops the 502/503/504 retry loop.
    replaySafe: false,
    retries: 0,
    expectedLatencyMs: 12_000,
    owner: "operations",
  },
  "ccli-submit-report": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 12_000,
    owner: "reports",
  },
  "run-calendar-sync": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    expectedLatencyMs: 12_000,
    owner: "integrations",
  },
  "integration-connect": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 8000,
    owner: "integrations",
  },
  "integration-dlq-resolve": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    // Stripe recovery reserves headroom inside the 150-second Edge runtime
    // for its final durable DLQ update after the nested processor returns.
    expectedLatencyMs: 120_000,
    timeoutMs: 150_000,
    owner: "integrations",
    userMessageOnFailure: "Unable to resolve the failed job. Please try again.",
  },
  "integration-health": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 8000,
    owner: "integrations",
  },
  "integration-sync": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 12_000,
    owner: "integrations",
  },
  "integration-webhook": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 4,
    expectedLatencyMs: 8000,
    owner: "integrations",
    userMessageOnFailure: "Webhook event sync is retrying in background.",
  },
  "qbo-api": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 12_000,
    owner: "integrations",
  },
  "qbo-export": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    expectedLatencyMs: 30_000,
    owner: "finance",
    userMessageOnFailure: "QuickBooks export failed. Please try again.",
  },
  "qbo-auth-start": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 5000,
    owner: "integrations",
  },
  "qbo-auth-callback": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 5000,
    owner: "integrations",
  },
  "zoho-api": {
    authRequired: true,
    policy: "background",
    retries: 3,
    expectedLatencyMs: 12_000,
    owner: "integrations",
  },
  "zoho-auth-start": {
    authRequired: false,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 5000,
    owner: "integrations",
  },
  "zoho-auth-callback": {
    authRequired: false,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 5000,
    owner: "integrations",
  },
  "zoho-save-credentials": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 8000,
    owner: "integrations",
  },
  "zoho-sync": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 12_000,
    owner: "integrations",
  },
  "stripe-create-checkout": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    // A non-2xx can mean Stripe just confirmed that this exact Session was
    // expired after the post-create disconnect barrier. Replaying the request
    // would retrieve that expired Session under the same idempotency key and
    // could hand its unusable URL to the donor. The endpoint and caller use a
    // durable attempt key for deliberate retries instead.
    replaySafe: false,
    expectedLatencyMs: 8000,
    owner: "payments",
    userMessageOnFailure: "Stripe checkout could not be started.",
  },
  "stripe-payment-methods": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 8000,
    owner: "payments",
    userMessageOnFailure: "Unable to manage payment methods. Please try again.",
  },
  "stripe-process-event": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 3,
    expectedLatencyMs: 8000,
    owner: "payments",
    userMessageOnFailure: "Stripe webhook processing is delayed.",
  },
  "stripe-backfill": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 12_000,
    owner: "payments",
    userMessageOnFailure: "Stripe backfill is retrying.",
  },
  // ── Platform Billing ──────────────────────────────────────
  "stripe-billing-create-checkout": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 8000,
    owner: "billing",
    userMessageOnFailure: "Unable to start billing checkout. Please try again.",
  },
  "stripe-billing-customer-portal": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 6000,
    owner: "billing",
    userMessageOnFailure: "Unable to open billing portal. Please try again.",
  },
  "stripe-billing-process-event": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 4,
    expectedLatencyMs: 8000,
    owner: "billing",
    userMessageOnFailure: "Billing webhook processing is delayed.",
  },
  // ── Connect Express ───────────────────────────────────────
  "stripe-connect-express-onboard": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    // Onboarding can include Stripe account creation + account-link creation.
    // Cold starts and upstream API latency regularly exceed 10s in production.
    expectedLatencyMs: 30_000,
    // `replaySafe: false`: a non-2xx here (e.g. `onboarding_link_stale`,
    // because another tab disconnected between this attempt's insert and its
    // lease claim) must surface to the CALLER as a failure, not trigger the
    // generic FunctionsHttpError fallback's own automatic re-POST. That
    // automatic retry reuses the request's own Idempotency-Key, so replaying
    // it after a disconnect gets Stripe's cached response for the
    // NOW-DISCONNECTED account back, re-inserts the mapping, and hands out a
    // live link — silently undoing the other tab's disconnect. The client's
    // own nonce cache (useStripeConnectExpress.tsx) cannot see or prevent
    // this: it is one physical fallback re-send inside a single
    // `invokeEdgeFunction` call, not a second call the cache's read/write
    // logic ever runs for (Codex P2, PR #6155, round 5).
    replaySafe: false,
    owner: "payments",
    userMessageOnFailure: "Unable to start Stripe onboarding. Please try again.",
  },
  "stripe-connect-express-process-event": {
    authRequired: false,
    policy: "fire-and-forget",
    retries: 4,
    expectedLatencyMs: 8000,
    owner: "payments",
    userMessageOnFailure: "Connect webhook processing is delayed.",
  },
  "stripe-connect-express-refresh": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 20_000,
    owner: "payments",
    userMessageOnFailure: "Unable to refresh onboarding link. Please try again.",
  },
  "stripe-connect-express-status": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 20_000,
    owner: "payments",
    userMessageOnFailure: "Unable to check account status.",
  },
  // ── Connect Standard ───────────────────────────────────────
  // Existing-account authorization and new-account onboarding are distinct.
  "stripe-connect-standard-start": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    expectedLatencyMs: 20_000,
    owner: "payments",
    userMessageOnFailure: "Unable to start linking your Stripe account.",
  },
  "stripe-connect-standard-onboard": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    // Onboarding can include Stripe account creation + account-link creation.
    // Cold starts and upstream API latency regularly exceed 10s in production.
    expectedLatencyMs: 30_000,
    // See the sibling stripe-connect-express-onboard entry above for the full
    // rationale — same mechanism, same fix, caught first here (Codex P2, PR
    // #6155, round 5) and mirrored to Express.
    replaySafe: false,
    owner: "payments",
    userMessageOnFailure: "Unable to start Stripe onboarding. Please try again.",
  },
  "stripe-connect-standard-callback": {
    authRequired: true,
    policy: "interactive",
    // This endpoint spends a single-use Stripe authorization code, and Stripe
    // revokes the account connection outright if one is consumed twice. So it
    // must never be transmitted more than once.
    //
    // `replaySafe: false` is what actually enforces that. `retries: 0` reads
    // like the guarantee and is not: `policy: "interactive"` already forces
    // `allowRetry` false regardless of this value, and the real replay path —
    // the FunctionsHttpError fallback — never consults `retries` at all. It
    // re-POSTs the identical body on ANY non-2xx. That is the mechanism
    // behind #6110's two exchanges ~2s apart on every attempt: not a
    // component remount, which would be intermittent, but this fallback
    // firing deterministically because the first call returned an error.
    replaySafe: false,
    retries: 0,
    expectedLatencyMs: 30_000,
    owner: "payments",
    userMessageOnFailure: "Unable to finish connecting your Stripe account. Please try again.",
  },
  "stripe-connect-disconnect": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 20_000,
    owner: "payments",
    userMessageOnFailure: "Unable to disconnect the Stripe account. Please try again.",
  },
  // ── Financial Connections ─────────────────────────────────
  "stripe-fc-create-session": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 8000,
    owner: "finance",
    userMessageOnFailure: "Unable to start bank account linking. Please try again.",
  },
  "stripe-fc-complete-link": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    // completeFCLinkWithRetries (useStripeFinancialConnections.tsx) already
    // implements its OWN outer retry loop — up to 3 whole-session attempts,
    // 1.5s apart — specifically because the raw single-call contract wasn't
    // resilient enough (Codex P1, PR #6139 round 2). Leaving the generic
    // FunctionsHttpError fallback enabled on top of that doubles every
    // non-2xx logical attempt into two physical requests with no benefit:
    // the outer loop is what retries the whole operation, not this.
    //
    // That doubling isn't just wasteful, it breaks the outer loop's own
    // contract. This endpoint runs at rate-limit profile "sync" (5 requests
    // per 60s, rate-limit.ts). A "total failure" attempt (every account
    // unresolved) returns a 500, which the fallback replays — so 3 logical
    // attempts that all hit that path cost 6 physical requests against a
    // 5-request budget, and the 3rd attempt's own fallback request is
    // deterministically rejected with 429 before ever reaching the handler —
    // exactly the "one last recovery chance" completeFCLinkWithRetries
    // promises the caller (Codex P2, PR #6139 round 7).
    //
    // `replaySafe: false` still recovers the real error message from the
    // FIRST response's body (readFunctionsHttpErrorBody in edge-functions.ts)
    // rather than needing the second request for it — see that mechanism's
    // own comment, added for the Standard Connect OAuth callback this
    // endpoint's replaySafe handling followed (#6110, retired then restored
    // for a compatibility window, #6151 / round 18).
    replaySafe: false,
    expectedLatencyMs: 10_000,
    owner: "finance",
    userMessageOnFailure: "Unable to complete bank account linking.",
  },
  "stripe-fc-disconnect": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    expectedLatencyMs: 6000,
    owner: "finance",
    userMessageOnFailure: "Unable to disconnect bank account. Please try again.",
  },
  "stripe-fc-sync-transactions": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 15_000,
    owner: "finance",
    userMessageOnFailure: "Transaction sync is being retried.",
  },
  "send-receipt": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 10_000,
    owner: "payments",
  },
  // Codex finding on PR #7009 (round 48, P1): with no override, this
  // destructive endpoint fell through to DEFAULT_REGISTRY_ENTRY, which sets
  // no `replaySafe` at all -- the fallback-on-non-2xx branch in
  // invokeEdgeFunction (see its own `isFunctionsHttpError(result.error) &&
  // metadata.replaySafe === false` gate) only skips the repost when this is
  // explicitly `false`; anything else (including undefined) reposts the
  // exact same body via invokeViaFetch. send-article-email dispatches
  // through comm-send BEFORE running its own tracking-row insert, so a
  // non-2xx from a later step (e.g. that insert throwing) can follow a
  // comm-send call that already succeeded -- and unlike send-transactional-
  // email/send-sms, useSendArticleEmail.ts passes no idempotencyKey at all,
  // so a replayed repost has nothing to dedupe it server-side. Disable the
  // replay outright rather than relying on a dedupe key this endpoint
  // doesn't have.
  "send-article-email": {
    authRequired: true,
    policy: "interactive",
    retries: 0,
    expectedLatencyMs: 12_000,
    owner: "communications",
    replaySafe: false,
  },
  // The handler validates the caller's bearer token itself (verify_jwt stays
  // false at the gateway), so the client must attach the session token. As
  // authRequired: false it went out tokenless and always 401'd (#6459).
  "submit-error-report": {
    authRequired: true,
    policy: "background",
    retries: 2,
    expectedLatencyMs: 8000,
    owner: "quality",
    userMessageOnFailure: "Error report is queued for retry.",
  },
  "songselect-search": {
    authRequired: true,
    policy: "interactive",
    expectedLatencyMs: 10_000,
    owner: "worship",
  },
  "songselect-planning-list": {
    authRequired: true,
    policy: "interactive",
    expectedLatencyMs: 10_000,
    owner: "worship",
  },
  "manage-push-subscription": {
    authRequired: true,
    policy: "interactive",
    retries: 1,
    expectedLatencyMs: 6000,
    owner: "notifications",
  },
};

export const EDGE_FUNCTION_REGISTRY: Record<EdgeFunctionName, EdgeFunctionMetadata> = EDGE_FUNCTION_NAMES.reduce(
  (result, name) => {
    const overrides = EDGE_FUNCTION_OVERRIDES[name] ?? {};
    result[name] = {
      ...DEFAULT_REGISTRY_ENTRY,
      ...overrides,
      name,
    };
    return result;
  },
  {} as Record<EdgeFunctionName, EdgeFunctionMetadata>,
);

export const getEdgeFunctionMetadata = (name: EdgeFunctionName): EdgeFunctionMetadata =>
  EDGE_FUNCTION_REGISTRY[name];
